import { useAuth } from "@clerk/clerk-react";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { VAStatus, VaOrganizationProfileStatus } from "schemas/dashboard";
import { routes } from "schemas/routes";

import ConnectGmailDialog from "components/ConnectGmailDialog";
import StyledVaPage from "components/Styled/VAPage";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { useTracking } from "utils/useTracking";

const SelfServeVaReview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const { trackVaStep } = useTracking();

  const { currentOrg, setCurrentOrg, setOrganizationVaProfile } = useContext(
    OrganizationUserContext,
  );
  const { emailHealth, fetchIntegrationHealth, userIntegration, scopes } =
    useContext(UserIntegrationsContext);
  const { userIntegrations } = useContext(OrganizationUserContext);

  const [userIntegrationDialogState, setUserIntegrationDialogState] =
    useState(false);
  const [loading, setLoading] = useState(false);

  const connectToGmail = () => {
    trackEvent("VA Onboarding Flow - Connect to Gmail Button Clicked");
    setUserIntegrationDialogState(true);
  };

  const approveOrganizationProfile = async () => {
    if (!emailHealth || currentOrg?.vaStatus === VAStatus.approved) {
      navigate(`/${routes.virtualAssistant}`);
      return;
    }

    setLoading(true);
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/approve`,
        "POST",
      );
      setOrganizationVaProfile((prev) => {
        if (prev) {
          prev.status = VaOrganizationProfileStatus.completed;
        }
        return prev;
      });

      setCurrentOrg((prev) => {
        if (prev) {
          prev.vaStatus = VAStatus.approved;
        }
        return prev;
      });
      navigate(`/${routes.virtualAssistant}`);
    } catch (error) {
      if (error?.message?.includes("follow up question")) {
        setAlert("Please answer follow-up questions for your niches", "error");
        navigate(`/${routes.nichesFollowUpSelfServeOnboarding}`);
        return;
      }
      setErrorAlert(error);
      localStorage.removeItem(`${currentOrg?.id}-va-step`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      fetchIntegrationHealth(currentOrg.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  useEffect(() => {
    if (emailHealth && currentOrg?.id) {
      approveOrganizationProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailHealth]);

  const onEmailConnected = async () => {
    setUserIntegrationDialogState(false);
  };

  return (
    <>
      <StyledVaPage
        title={
          emailHealth
            ? "You're successfully onboarded 🚀"
            : "Connect your Gmail Account"
        }
        description={
          emailHealth
            ? "Click go to dashboard to see your AI virtual assistant in action"
            : "Providing access to your Gmail account allows Bento to send emails on your behalf"
        }
        body={<></>}
        stepNumber={5}
        onBack={() => {
          navigate(`/${routes.preferencesSelfServeOnboarding}`);
          trackVaStep(routes.preferencesSelfServeOnboarding);
        }}
        buttonBack={"Back to Preferences"}
        buttonNext={emailHealth ? "Go to dashboard" : "Connect to Gmail"}
        onNext={() => {
          if (!emailHealth) {
            connectToGmail();
          } else {
            approveOrganizationProfile();
            navigate(`/${routes.virtualAssistant}${location.search}`);
          }
        }}
        loading={loading}
      />
      <ConnectGmailDialog
        openIntegrationDialog={userIntegrationDialogState}
        setOpenIntegrationDialog={(newOpen: boolean) =>
          setUserIntegrationDialogState(newOpen)
        }
        userIntegrations={userIntegrations}
        userIntegration={userIntegration}
        scopes={scopes}
        emailHealth={emailHealth}
        onSuccessAction={onEmailConnected}
        title="Connect your Gmail account"
      />
    </>
  );
};

export default SelfServeVaReview;
